import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  CfsTemplateError,
  CfsTemplateErrorData,
  CfsTemplateMediaConfig,
  defaultCfsTemplateMediaConfig,
  mediaReadyDelay,
  TemplateAnimationType,
  VideoPlayer
} from "clearline-common";
import { VideoPlayerErrorMessages, VideoPlayerErrorTitles } from "../video-player-error-messages";
import { CfsTemplateErrorDataFields, CfsTemplateErrorTypes, generateCfsTemplateError } from "../../cfs-template-errors";

@Component({
  selector: "lib-html-video-player",
  templateUrl: "./html-video-player.component.html",
  styleUrls: ["./html-video-player.component.scss"],
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HtmlVideoPlayerComponent implements VideoPlayer, AfterViewInit {
  @Input() mediaConfig: CfsTemplateMediaConfig = { ...defaultCfsTemplateMediaConfig, autoplay: true };
  @Input() url = "";
  @Input() thumbnailUrl = "";
  @Input() animationType: TemplateAnimationType = TemplateAnimationType.OnDemandAnimated;
  @Input() displayTime = 0;
  @Input() contentDuration = 0;

  @Output() ready = new EventEmitter();
  @Output() error = new EventEmitter<CfsTemplateError>();

  @ViewChild("videoPlayer") player?: ElementRef<HTMLVideoElement>;

  @HostBinding("class.cover-parent") get isCoverParent() {
    return !!this.mediaConfig.coverParent;
  }

  private mediaCanPlayEventListener: (() => void) | null = null;
  private playerErrorOccurred = false;

  ngAfterViewInit(): void {
    const videoElement: HTMLVideoElement | null = this.getVideoPlayer();

    if (videoElement) {
      const successCallback = () => {
        this.ready.emit();

        if (!!this.mediaConfig.autoplay) {
          this.play();
        }
      };

      this.prepareMediaElementPlayback(successCallback, videoElement, mediaReadyDelay);

      if (this.animationType === TemplateAnimationType.Animated || this.animationType === TemplateAnimationType.OnDemandAnimated) {
        const { muted = true, autoplay, loop = false } = this.mediaConfig;

        videoElement.muted = muted;
        videoElement.loop = loop || autoplay || !this.contentDuration || !!(this.displayTime && this.displayTime > this.contentDuration);
      }
    }
  }

  play(): void {
    const videoElement: HTMLVideoElement | null = this.getVideoPlayer() as HTMLVideoElement;

    if (videoElement) {
      const successCallback = () => videoElement.play().then();

      this.prepareMediaElementPlayback(successCallback, videoElement, 0);
    }
  }

  pause(): void {
    const videoElement: HTMLVideoElement | null = this.getVideoPlayer() as HTMLVideoElement;

    if (videoElement) {
      this.removeMediaPlayEventListener(videoElement);

      if (!videoElement.paused) {
        videoElement.pause();
      }
    }
  }

  stop(): void {
    const videoElement: HTMLVideoElement | null = this.getVideoPlayer() as HTMLVideoElement;

    this.pause();

    if (videoElement) {
      videoElement.currentTime = 0;
    }
  }

  restart(): void {
    const videoElement: HTMLVideoElement | null = this.getVideoPlayer() as HTMLVideoElement;

    if (videoElement) {
      videoElement.currentTime = 0;
    }

    this.play();
  }

  getVideoDuration(): Promise<number> {
    const videoElement: HTMLVideoElement | null = this.getVideoPlayer() as HTMLVideoElement;

    return Promise.resolve(videoElement?.duration || 0);
  }

  getThumbnailUrl(): Promise<string> {
    const videoElement: HTMLVideoElement | null = this.getVideoPlayer() as HTMLVideoElement;

    return Promise.resolve(videoElement?.poster || "");
  }

  hasError(): boolean {
    return this.playerErrorOccurred;
  }

  protected onError(event: ErrorEvent): void {
    this.playerErrorOccurred = true;
    const errorMessage = this.generateErrorMessage(event);
    const errorData: CfsTemplateErrorData = { field: CfsTemplateErrorDataFields.Url, value: this.url };
    const templateError: CfsTemplateError = generateCfsTemplateError(CfsTemplateErrorTypes.VideoLoad, errorMessage, errorData);

    this.error.emit(templateError);
  }

  private generateErrorMessage(error: ErrorEvent): string {
    const content = error.message || VideoPlayerErrorMessages.Load;

    return `${VideoPlayerErrorTitles.Html5}: ${content}`;
  }

  private getVideoPlayer(): HTMLVideoElement | null {
    return this.player?.nativeElement || null;
  }

  private prepareMediaElementPlayback(callback: () => void, mediaElement: HTMLMediaElement, timeout: number): void {
    if (mediaElement) {
      this.removeMediaPlayEventListener(mediaElement);

      if (this.isMediaElementDataLoaded(mediaElement)) {
        callback();
      } else {
        this.mediaCanPlayEventListener = () => {
          setTimeout(() => callback(), timeout);
          this.removeMediaPlayEventListener(mediaElement);
        };

        mediaElement.addEventListener("canplaythrough", this.mediaCanPlayEventListener.bind(this), { once: true });
      }
    }
  }

  private removeMediaPlayEventListener(mediaElement: HTMLMediaElement): void {
    if (this.mediaCanPlayEventListener) {
      mediaElement.removeEventListener("canplaythrough", this.mediaCanPlayEventListener);
      this.mediaCanPlayEventListener = null;
    }
  }

  private isMediaElementDataLoaded(mediaElement: HTMLMediaElement): boolean {
    const successState = 4;

    return mediaElement.readyState >= successState;
  }
}
